import { useMemo } from 'react'
import { useQueries } from 'react-query'

import { MetricsApi, QUERY_KEYS } from '@api'
import { useDashboardFilters, useUser } from '@contexts'
import { ImplementationTimeUtils } from '@utils'

import { CarriersApi } from '../useCarriers/api'
import { SuppliersApi } from '../useSuppliers/api'
import { Carrier, Supplier } from '../../types'
import { DailyMetricsResponse, WeeklyMetricsResponse } from '../../api/metrics'

const STALE_TIME = 60000

const { inImplementationZone } = ImplementationTimeUtils

interface DashboardData {
  dailyData: DailyMetricsResponse | undefined
  weeklyData: WeeklyMetricsResponse | undefined
  allSuppliers: Supplier[] | undefined
  allCarriers: Carrier[] | undefined
  isLoading: boolean
  isLoadingDaily: boolean
  isLoadingWeekly: boolean
  isError: boolean
}

/**
 * Merge daily metrics with full supplier/carrier lists.
 * - Suppliers from metrics are preserved with their real counts
 * - Missing suppliers from allSuppliers are added with zero counts
 * - Carriers within each supplier are similarly merged
 */
export function mergeDailyData(
  metricsData: DailyMetricsResponse | undefined,
  allSuppliers: Supplier[] | undefined,
  allCarriers: Carrier[] | undefined,
): DailyMetricsResponse {
  if (!metricsData || !allSuppliers) {
    return { suppliers: [] }
  }

  // Build map of all suppliers with base structure
  const supplierMap = new Map<
    number,
    {
      supplierId: number
      tradeName: string
      orders: number
      breaches: number
      carriers: Map<number, { carrierId: number; tradeName: string; confirmedOrders: number }>
    }
  >()

  // Initialize with all active suppliers (zero counts)
  for (const supplier of allSuppliers) {
    if (supplier.disableAt) continue // Skip disabled suppliers

    supplierMap.set(supplier.id, {
      supplierId: supplier.id,
      tradeName: supplier.tradeName,
      orders: 0,
      breaches: 0,
      carriers: new Map(),
    })
  }

  // Initialize carriers for each supplier from allCarriers
  if (allCarriers) {
    for (const carrier of allCarriers) {
      if (carrier.disableAt) continue // Skip disabled carriers

      // Add this carrier to ALL suppliers (carrier-supplier relationship not tracked)
      for (const [, supplierData] of supplierMap) {
        supplierData.carriers.set(carrier.id, {
          carrierId: carrier.id,
          tradeName: carrier.tradeName,
          confirmedOrders: 0,
        })
      }
    }
  }

  // Apply metrics data (overwrites zeros where there's activity)
  for (const metricSupplier of metricsData.suppliers) {
    const existing = supplierMap.get(metricSupplier.supplierId)

    if (existing) {
      // Update existing supplier with real counts
      existing.orders = metricSupplier.orders
      existing.breaches = metricSupplier.breaches

      // Apply carrier metrics
      for (const metricCarrier of metricSupplier.carriers) {
        existing.carriers.set(metricCarrier.carrierId, {
          carrierId: metricCarrier.carrierId,
          tradeName: metricCarrier.tradeName,
          confirmedOrders: metricCarrier.confirmedOrders,
        })
      }
    } else {
      // Supplier not in allSuppliers (disabled but with activity) - add it
      const carrierMap = new Map<
        number,
        { carrierId: number; tradeName: string; confirmedOrders: number }
      >()

      // Add all carriers first
      if (allCarriers) {
        for (const carrier of allCarriers) {
          if (!carrier.disableAt) {
            carrierMap.set(carrier.id, {
              carrierId: carrier.id,
              tradeName: carrier.tradeName,
              confirmedOrders: 0,
            })
          }
        }
      }

      // Apply metric carriers
      for (const metricCarrier of metricSupplier.carriers) {
        carrierMap.set(metricCarrier.carrierId, {
          carrierId: metricCarrier.carrierId,
          tradeName: metricCarrier.tradeName,
          confirmedOrders: metricCarrier.confirmedOrders,
        })
      }

      supplierMap.set(metricSupplier.supplierId, {
        supplierId: metricSupplier.supplierId,
        tradeName: metricSupplier.tradeName,
        orders: metricSupplier.orders,
        breaches: metricSupplier.breaches,
        carriers: carrierMap,
      })
    }
  }

  // Convert map to array response
  const suppliers = Array.from(supplierMap.values()).map(supplier => ({
    supplierId: supplier.supplierId,
    tradeName: supplier.tradeName,
    orders: supplier.orders,
    breaches: supplier.breaches,
    carriers: Array.from(supplier.carriers.values()),
  }))

  return { suppliers }
}

/**
 * Merge weekly metrics with full supplier list.
 * - Suppliers from metrics are preserved with their real day counts
 * - Missing suppliers from allSuppliers are added with 7 days of zeros
 */
function mergeWeeklyData(
  metricsData: WeeklyMetricsResponse | undefined,
  allSuppliers: Supplier[] | undefined,
  weekStart: Date,
  gmtZone: number,
): WeeklyMetricsResponse {
  if (!metricsData || !allSuppliers) {
    const result: WeeklyMetricsResponse = { suppliers: [] }
    if (metricsData?.transfers !== undefined) {
      result.transfers = metricsData.transfers
    }
    return result
  }

  // Generate week dates anchored to the implementation timezone, so the day keys match the
  // implementation-local days returned by the backend regardless of the device timezone.
  const weekStartLocal = inImplementationZone(weekStart, gmtZone).startOf('day')
  const weekDates: Date[] = []
  for (let i = 0; i < 7; i++) {
    weekDates.push(weekStartLocal.add(i, 'day').toDate())
  }

  // Build map of all suppliers
  const supplierMap = new Map<
    number,
    {
      supplierId: number
      tradeName: string
      days: { date: Date; orders: number; breaches: number }[]
    }
  >()

  // Initialize with all active suppliers (7 days of zeros)
  for (const supplier of allSuppliers) {
    if (supplier.disableAt) continue

    supplierMap.set(supplier.id, {
      supplierId: supplier.id,
      tradeName: supplier.tradeName,
      days: weekDates.map(date => ({ date: new Date(date), orders: 0, breaches: 0 })),
    })
  }

  // Apply metrics data
  for (const metricSupplier of metricsData.suppliers) {
    const existing = supplierMap.get(metricSupplier.supplierId)

    if (existing) {
      // Update existing supplier - map days by date
      const dayMap = new Map(existing.days.map(d => [d.date.toISOString().split('T')[0], d]))

      for (const metricDay of metricSupplier.days) {
        const dateKey = new Date(metricDay.date).toISOString().split('T')[0]
        const day = dayMap.get(dateKey)
        if (day) {
          day.orders = metricDay.orders
          day.breaches = metricDay.breaches
        }
      }
    } else {
      // Supplier not in allSuppliers (disabled but with activity) - add it
      supplierMap.set(metricSupplier.supplierId, {
        supplierId: metricSupplier.supplierId,
        tradeName: metricSupplier.tradeName,
        days: metricSupplier.days.map(d => ({
          date: new Date(d.date),
          orders: d.orders,
          breaches: d.breaches,
        })),
      })
    }
  }

  const suppliers = Array.from(supplierMap.values()).map(s => ({
    supplierId: s.supplierId,
    tradeName: s.tradeName,
    days: s.days,
  }))

  let transfersMerged: WeeklyMetricsResponse['transfers']
  if (metricsData.transfers !== undefined) {
    const transferMap = new Map<
      number,
      {
        supplierId: number
        tradeName: string
        days: { date: Date; orders: number; breaches: number }[]
      }
    >()

    for (const supplier of allSuppliers) {
      if (supplier.disableAt || supplier.sapId) continue

      transferMap.set(supplier.id, {
        supplierId: supplier.id,
        tradeName: supplier.tradeName,
        days: weekDates.map(date => ({ date: new Date(date), orders: 0, breaches: 0 })),
      })
    }

    for (const metricSupplier of metricsData.transfers) {
      const existing = transferMap.get(metricSupplier.supplierId)

      if (existing) {
        const dayMap = new Map(existing.days.map(d => [d.date.toISOString().split('T')[0], d]))

        for (const metricDay of metricSupplier.days) {
          const dateKey = new Date(metricDay.date).toISOString().split('T')[0]
          const day = dayMap.get(dateKey)
          if (day) {
            day.orders = metricDay.orders
            day.breaches = metricDay.breaches
          }
        }
      } else {
        transferMap.set(metricSupplier.supplierId, {
          supplierId: metricSupplier.supplierId,
          tradeName: metricSupplier.tradeName,
          days: metricSupplier.days.map(d => ({
            date: new Date(d.date),
            orders: d.orders,
            breaches: d.breaches,
          })),
        })
      }
    }

    transfersMerged = Array.from(transferMap.values()).sort((a, b) => a.supplierId - b.supplierId)
  }

  const result: WeeklyMetricsResponse = { suppliers }
  if (transfersMerged !== undefined) {
    result.transfers = transfersMerged
  }
  return result
}

/**
 * Hook that combines metrics with full supplier/carrier lists.
 * Backend returns only suppliers/carriers with activity;
 * frontend completes the list using full entity endpoints.
 */
export function useDashboardData(): DashboardData {
  const { weekStart, selectedDay, tripType, supplierId, carrierId } = useDashboardFilters()
  const { state } = useUser()
  const gmtZone = state?.company?.gmtZone ?? 0

  // Fetch all data in parallel
  const queries = useQueries([
    {
      queryKey: [
        QUERY_KEYS.METRICS_WEEKLY,
        weekStart.toISOString(),
        tripType,
        gmtZone,
        supplierId,
        carrierId,
      ],
      queryFn: () => MetricsApi.getWeekly({ weekStart, tripType, gmtZone, supplierId, carrierId }),
      staleTime: STALE_TIME,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
    {
      queryKey: [
        QUERY_KEYS.METRICS_DAILY,
        selectedDay.toISOString(),
        tripType,
        gmtZone,
        supplierId,
        carrierId,
      ],
      queryFn: () =>
        MetricsApi.getDaily({
          date: selectedDay,
          tripType: [tripType],
          gmtZone,
          supplierId,
          carrierId,
        }),
      staleTime: STALE_TIME,
      refetchOnWindowFocus: false,
      enabled: !!selectedDay,
      keepPreviousData: true,
    },
    {
      queryKey: [QUERY_KEYS.SUPPLIERS, { disabled: false }],
      queryFn: () => SuppliersApi.getSuppliers({ filters: { disabled: false } }),
      staleTime: STALE_TIME,
    },
    {
      queryKey: [QUERY_KEYS.ALL_CARRIERS, { disabled: false, limit: 1000 }],
      queryFn: () =>
        CarriersApi.getCarriersWithFilters({ filters: { disabled: false, limit: 1000 } }),
      staleTime: STALE_TIME,
    },
  ])

  const [weeklyQuery, dailyQuery, suppliersQuery, carriersQuery] = queries

  const suppliersForMerge = useMemo(() => {
    const all = suppliersQuery.data
    if (!all) return all
    if (supplierId !== undefined) return all.filter(s => s.id === supplierId)
    // Carrier-only: skip zero-padding so the grid shows activity for that carrier only.
    if (carrierId !== undefined) return []
    return all
  }, [suppliersQuery.data, supplierId, carrierId])

  const carriersForMerge = useMemo(() => {
    const all = carriersQuery.data?.carriers
    if (!all || carrierId === undefined) return all
    return all.filter(c => c.id === carrierId)
  }, [carriersQuery.data?.carriers, carrierId])

  const weeklyData = useMemo(
    () => mergeWeeklyData(weeklyQuery.data, suppliersForMerge, weekStart, gmtZone),
    [weeklyQuery.data, suppliersForMerge, weekStart, gmtZone],
  )

  const dailyData = useMemo(
    () => mergeDailyData(dailyQuery.data, suppliersForMerge, carriersForMerge),
    [dailyQuery.data, suppliersForMerge, carriersForMerge],
  )

  const isLoading = queries.some(q => q.isLoading)
  const isError = queries.some(q => q.isError)

  return {
    dailyData,
    weeklyData,
    allSuppliers: suppliersQuery.data,
    allCarriers: carriersQuery.data?.carriers,
    isLoading,
    isLoadingDaily: dailyQuery.isLoading || dailyQuery.isFetching,
    isLoadingWeekly: weeklyQuery.isLoading || weeklyQuery.isFetching,
    isError,
  }
}
